"use client";

import { useEffect, useRef, useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { Pin, PinOff, Trash2, Tag as TagIcon, X, Check, Loader2 } from "lucide-react";
import {
  updateNote,
  softDeleteNote,
  addTagToNote,
  removeTagFromNote,
} from "@/app/actions";
import type { NoteWithTags } from "@/lib/queries";
import { type NoteColor, noteColorClass, resolveColor } from "@/lib/colors";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ColorPicker } from "@/components/notes/color-picker";

type SaveState = "idle" | "saving" | "saved";

export function NoteEditor({ note, onClose }: { note: NoteWithTags | null; onClose: () => void }) {
  const router = useRouter();
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [color, setColor] = useState<NoteColor>("default");
  const [tagInput, setTagInput] = useState("");
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [, startTransition] = useTransition();

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seededId = useRef<string | null>(null);
  const latest = useRef({ title: "", content: "", color: "default" as NoteColor });

  // Sembrar el estado local cuando cambia la nota abierta (no en cada refresh).
  useEffect(() => {
    if (note && note.id !== seededId.current) {
      seededId.current = note.id;
      setTitle(note.title ?? "");
      setContent(note.content ?? "");
      setColor(resolveColor(note.color));
      setSaveState("idle");
    }
    if (!note) seededId.current = null;
  }, [note]);

  // Mantener la última versión editada fuera del render (para el guardado diferido).
  useEffect(() => {
    latest.current = { title, content, color };
  }, [title, content, color]);

  function persist() {
    if (!note) return;
    setSaveState("saving");
    startTransition(async () => {
      const res = await updateNote({ id: note.id, ...latest.current });
      if (!res.ok) {
        toast.error(res.error);
        setSaveState("idle");
        return;
      }
      setSaveState("saved");
      router.refresh();
    });
  }

  // Autoguardado con debounce ante cambios de título/contenido/color.
  function scheduleSave() {
    if (timer.current) clearTimeout(timer.current);
    setSaveState("saving");
    timer.current = setTimeout(persist, 700);
  }

  function flushAndClose() {
    if (timer.current) clearTimeout(timer.current);
    if (note && (title !== (note.title ?? "") || content !== (note.content ?? "") || color !== resolveColor(note.color))) {
      persist();
    }
    onClose();
  }

  function togglePin() {
    if (!note) return;
    startTransition(async () => {
      const res = await updateNote({ id: note.id, pinned: !note.pinned });
      if (res.ok) router.refresh();
      else toast.error(res.error);
    });
  }

  function moveToTrash() {
    if (!note) return;
    if (timer.current) clearTimeout(timer.current);
    const id = note.id;
    onClose();
    startTransition(async () => {
      const res = await softDeleteNote(id);
      if (res.ok) {
        router.refresh();
        toast.success("Movida a la papelera");
      } else toast.error(res.error);
    });
  }

  function commitTag() {
    const name = tagInput.trim();
    if (!name || !note) return;
    setTagInput("");
    startTransition(async () => {
      const res = await addTagToNote(note.id, name);
      if (res.ok) router.refresh();
      else toast.error(res.error);
    });
  }

  function removeTag(tagId: string) {
    if (!note) return;
    startTransition(async () => {
      const res = await removeTagFromNote(note.id, tagId);
      if (res.ok) router.refresh();
      else toast.error(res.error);
    });
  }

  const open = note !== null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) flushAndClose(); }}>
      <DialogContent
        showCloseButton={false}
        className={cn("max-w-lg gap-0 p-0 sm:max-w-lg", noteColorClass(color))}
      >
        {note && (
          <div className="flex max-h-[85vh] flex-col">
            <div className="flex items-start gap-2 px-4 pt-4">
              <Input
                value={title}
                onChange={(e) => { setTitle(e.target.value); scheduleSave(); }}
                placeholder="Título"
                className="h-9 border-0 bg-transparent px-1 font-display text-xl font-medium shadow-none focus-visible:ring-0 dark:bg-transparent"
              />
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={note.pinned ? "Quitar fijado" : "Fijar nota"}
                title={note.pinned ? "Quitar fijado" : "Fijar"}
                className={cn("shrink-0", note.pinned && "text-primary")}
                onClick={togglePin}
              >
                {note.pinned ? <Pin className="fill-current" /> : <PinOff />}
              </Button>
            </div>

            <DialogTitle className="sr-only">{title || "Editar nota"}</DialogTitle>

            <div className="flex-1 overflow-y-auto px-4 py-2">
              <Textarea
                value={content}
                onChange={(e) => { setContent(e.target.value); scheduleSave(); }}
                placeholder="Empieza a escribir…"
                className="min-h-48 resize-none border-0 bg-transparent px-1 text-[15px] leading-relaxed shadow-none focus-visible:ring-0 dark:bg-transparent"
              />

              <div className="mt-3 flex flex-wrap items-center gap-1.5">
                {note.tags.map((t) => (
                  <Badge key={t.id} variant="secondary" className="gap-1 bg-foreground/10 pr-1 text-foreground/80">
                    <TagIcon className="size-2.5" />
                    {t.name}
                    <button
                      type="button"
                      onClick={() => removeTag(t.id)}
                      aria-label={`Quitar etiqueta ${t.name}`}
                      className="ml-0.5 rounded-full p-0.5 hover:bg-foreground/10"
                    >
                      <X className="size-2.5" />
                    </button>
                  </Badge>
                ))}
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault();
                      commitTag();
                    }
                  }}
                  onBlur={commitTag}
                  placeholder="Añadir etiqueta…"
                  className="h-7 w-32 border-0 bg-transparent px-1 text-xs shadow-none focus-visible:ring-0 dark:bg-transparent"
                />
              </div>
            </div>

            <div className="flex items-center justify-between gap-2 border-t border-foreground/10 px-3 py-2">
              <div className="flex items-center gap-0.5">
                <ColorPicker value={color} onChange={(c) => { setColor(c); scheduleSave(); }} align="start" />
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Mover a la papelera"
                  title="Mover a la papelera"
                  onClick={moveToTrash}
                >
                  <Trash2 />
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <SaveIndicator state={saveState} updatedAt={note.updatedAt} />
                <Button size="sm" onClick={flushAndClose}>Listo</Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SaveIndicator({ state, updatedAt }: { state: SaveState; updatedAt: Date | string }) {
  if (state === "saving")
    return (
      <span className="flex items-center gap-1 text-xs text-muted-foreground">
        <Loader2 className="size-3 animate-spin" /> Guardando…
      </span>
    );
  if (state === "saved")
    return (
      <span className="flex items-center gap-1 text-xs text-muted-foreground">
        <Check className="size-3" /> Guardado
      </span>
    );
  return <span className="text-xs text-muted-foreground">Editada {formatDateTime(updatedAt)}</span>;
}
